import { FileText } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@tellescope/ui";
import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { TemplateFormDialogContentsSectionForm } from "../types";
import { CarePlanItemPicker } from "./CarePlanItemPicker";
import { CarePlanSelectedList } from "./CarePlanSelectedList";
import { CarePlanFormItem } from "./CarePlanFormItem";

export interface CarePlanFormsSectionProps {
  form: TemplateFormDialogContentsSectionForm<"carePlanForms">;
}

export const CarePlanFormsSection = ({ form }: CarePlanFormsSectionProps) => {
  const { t } = useTranslation("calendar");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const formsQuery = useTellescopeInfiniteQuery({
    model: "forms",
    method: "getSome",
    request: {
      search: debouncedSearch ? { query: debouncedSearch } : undefined,
    },
    select: (data) => data.pages.flat(),
    placeholderData: (prev) => prev,
  });

  const selectedIds = useMemo(() => new Set(form.values.carePlanForms ?? []), [form.values.carePlanForms]);
  const forms = useMemo(() => formsQuery.data ?? [], [formsQuery.data]);
  const isLoading = formsQuery.isFetching || search !== debouncedSearch;

  const selectableForms = useMemo(
    () =>
      forms.reduce<TellescopeModelFromName<"forms">[]>((acc, curr) => {
        if (selectedIds.has(curr.id)) {
          return acc;
        }
        acc.push(curr);
        return acc;
      }, []),
    [forms, selectedIds]
  );

  const handleAdd = (id: string) => {
    if (selectedIds.has(id)) return;
    form.setFieldValue("carePlanForms", [...selectedIds, id]);
    setSearch("");
    setPopoverOpen(false);
  };

  const handleRemove = (id: string) => {
    form.setFieldValue(
      "carePlanForms",
      Array.from(selectedIds).filter((f) => f !== id)
    );
  };

  return (
    <div className="space-y-4">
      <CarePlanItemPicker
        icon={<FileText size={16} />}
        label={t("Forms")}
        addButtonText={t("Add Form")}
        searchPlaceholder={t("Search forms...")}
        emptyText={t("No forms found.")}
        searchValue={search}
        onSearchChange={setSearch}
        isLoading={isLoading}
        popoverOpen={popoverOpen}
        onPopoverOpenChange={setPopoverOpen}
        items={selectableForms}
        getItemId={(f) => f.id}
        onSelectItem={handleAdd}
        renderItem={(f) => f.title}
      />
      <CarePlanSelectedList
        ids={form.values.carePlanForms ?? []}
        emptyText={t("No forms selected. Use the dropdown above to add forms.")}
        onRemove={handleRemove}
        ItemComponent={CarePlanFormItem}
      />
    </div>
  );
};
